import HomeBarButton from '@core/dashboard/components/HomeBarButton';
import { ScreenShareIcon } from '@icons';
import React, { useCallback } from 'react';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useCommunications } from '@core/communications/providers/CommunicationsProvider';
import { useTranslation } from 'react-i18next';

import { useConference } from '@core/dashboard/components/DashboardContent/Dolby';
import { sendCustomSentryBreadcrumb, setSentryError } from '@helpers/sentry';
import { AnalyticsType } from '@interfaces/analytics';
import { getCatchErrorMessage } from '@helpers/error';
import {
  getErrorNotification,
  useNotification,
} from '@components/Notification';

const ScreenShareButton = () => {
  const { ueScreenScreenName, ueScreenMediaStreams } = useConference();

  const { t: translate } = useTranslation();
  const { showNotification } = useNotification();

  const { currentParticipant, trackEventAnalytics } = useMytaverseEvent();

  const {
    startScreenSharing,
    stopScreenSharing,
    isSharingScreen,
    isInConference,
    isJoiningConference,
  } = useCommunications();

  const currentParticipantId = currentParticipant?.participantId || '';
  const currentScreenSharing = !!ueScreenMediaStreams.find(
    (s) => s.participantId === currentParticipantId,
  );

  const handleShareScreenClick = useCallback(async () => {
    try {
      trackEventAnalytics(AnalyticsType.NavbarButtonClick, { name: 'screen-sharing' });

      if (isSharingScreen) {
        sendCustomSentryBreadcrumb({ message: 'Stop zoom share screen' });
        console.log('Stop zoom share screen');
        await stopScreenSharing();
        trackEventAnalytics(AnalyticsType.ScreenShareFinish, {
          region: currentParticipant?.region?.region,
          isPrivate: currentParticipant?.region?.isSeatRegion || false,
        });
      } else {
        sendCustomSentryBreadcrumb({ message: 'Start zoom share screen' });
        console.log('Start zoom share screen');
        await startScreenSharing();
        trackEventAnalytics(AnalyticsType.ScreenShareStart, {
          region: currentParticipant?.region?.region,
          isPrivate: currentParticipant?.region?.isSeatRegion || false,
        });
      }
    } catch (error: unknown) {
      console.log(getCatchErrorMessage(error));
      setSentryError(error);
      showNotification(
        getErrorNotification({
          message: translate('notifications.video.sharing.stopped'),
        }),
      );
    }
  }, [
    isSharingScreen,
    trackEventAnalytics,
    startScreenSharing,
    stopScreenSharing,
    sendCustomSentryBreadcrumb,
    currentParticipant,
  ]);

  const translatedShareTooltip =
    isSharingScreen && currentScreenSharing
      ? 'tooltips.stopShareScreen'
      : 'tooltips.shareScreen';

  return (
    <HomeBarButton
      label={translate(translatedShareTooltip)}
      isActive={isSharingScreen && currentScreenSharing}
      icon={<ScreenShareIcon />}
      isLoading={isJoiningConference}
      disabled={
        !isInConference ||
        (!!ueScreenMediaStreams.length && !currentScreenSharing)
      }
      handleClick={handleShareScreenClick}
    />
  );
};

export default ScreenShareButton;
